import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { formatCurrency } from "../../sdk/utils/display";
import { SliderConfirmBar, NumberPad } from "./fragments";

/**
 * Transfer flow parameters
 */
export interface TransferFlowParams {
  type: "transfer";
  from: string;
  to: string;
  amount: number;
  // marginType is not needed - when from is "spot-wallet", to must be a bucket
  // and the bucket's marginType can be determined from the bucket data
}

/**
 * Create bucket flow parameters.
 * collateralAsset: symbol for collateral (e.g. USDC, USDT). Proto uses collateralAssetIndex.
 */
export interface CreateBucketFlowParams {
  type: "create";
  from: string;
  collateralAsset: string;
  marginType: "cross" | "isolated";
  amount: number;
}

/**
 * Union type for transfer modal confirm parameters
 */
export type TransferModalConfirmParams =
  | TransferFlowParams
  | CreateBucketFlowParams;

/**
 * Transfer Modal Props
 */
export interface TransferModalProps {
  isOpen: boolean;
  fromValue: string;
  toValue: string;
  options: Array<{ value: string; label: string }>;
  bucketsData: Array<{ bucketId: string; totalEquity: number }>;
  transferAmount: number;
  createBucketAmount: number;
  createBucketAsset: string;
  createMarginType: "cross" | "isolated";
  transferMarginType?: "cross" | "isolated";
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onTransferAmountChange: (amount: number) => void;
  onCreateBucketAmountChange: (amount: number) => void;
  onCreateBucketAssetChange: (value: string) => void;
  onCreateMarginTypeChange: (value: "cross" | "isolated") => void;
  onTransferMarginTypeChange?: (value: "cross" | "isolated") => void;
  onConfirm: (params: TransferModalConfirmParams) => void;
  onCancel: () => void;
  spotWalletBalance?: number;
  activeTab?: "transfer" | "create";
  onActiveTabChange?: (tab: "transfer" | "create") => void;
  isTransferring?: boolean;
  onOpen?: () => void;
  collections?: Array<{ bucketId: string; marginType?: string }>;
  /** Called when the modal overlay (backdrop) is clicked. Default: no-op. */
  onOverlayClick?: () => void;
}

/**
 * Transfer Modal
 */
export function TransferModal({
  isOpen,
  fromValue,
  toValue,
  options,
  bucketsData,
  transferAmount,
  createBucketAmount,
  createBucketAsset,
  createMarginType,
  transferMarginType,
  onFromChange,
  onToChange,
  onTransferAmountChange,
  onCreateBucketAmountChange,
  onCreateBucketAssetChange,
  onCreateMarginTypeChange,
  onTransferMarginTypeChange,
  onConfirm,
  onCancel,
  spotWalletBalance = 0,
  activeTab,
  onActiveTabChange,
  isTransferring = false,
  onOpen,
  collections,
  onOverlayClick = () => {},
}: TransferModalProps): JSX.Element | null {
  // Use internal state if not provided from parent (for backward compatibility)
  const [internalActiveTab, setInternalActiveTab] = useState<
    "transfer" | "create"
  >("transfer");
  const currentActiveTab = activeTab ?? internalActiveTab;
  const handleActiveTabChange = onActiveTabChange ?? setInternalActiveTab;

  // Track previous isOpen state to detect when modal opens
  const prevIsOpenRef = useRef<boolean>(false);

  // Number pad state
  const [isNumberPadOpen, setIsNumberPadOpen] = useState<boolean>(false);
  const [numberPadType, setNumberPadType] = useState<"transfer" | "create">(
    "transfer",
  );

  // Slider confirm state
  const [isSliderConfirmed, setIsSliderConfirmed] = useState<boolean>(false);

  // Refresh spot wallet balance when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && onOpen) {
      // Modal just opened - refresh the data
      onOpen();
    }
    prevIsOpenRef.current = isOpen;
    // Reset slider confirmation when modal opens/closes or tab changes
    if (!isOpen) {
      setIsSliderConfirmed(false);
    }
  }, [isOpen, onOpen, currentActiveTab]);

  // Get transferable funds for selected bucket or spot wallet
  const selectedBucket = useMemo(() => {
    if (fromValue === "spot-wallet") {
      return null; // Spot wallet is not in bucketsData
    }
    return bucketsData.find((b) => b.bucketId === fromValue);
  }, [bucketsData, fromValue]);

  // Get the selected bucket's margin type from collections
  const selectedBucketMarginType = useMemo(() => {
    if (fromValue === "spot-wallet" || !collections) {
      return null;
    }
    const bucket = collections.find((b) => b.bucketId === fromValue);
    return bucket?.marginType?.toLowerCase() || null;
  }, [collections, fromValue]);

  // Calculate max transferable - use spot wallet balance if selected
  const maxTransferable = useMemo(() => {
    if (fromValue === "spot-wallet") {
      return spotWalletBalance;
    }
    return selectedBucket?.totalEquity || 0;
  }, [fromValue, spotWalletBalance, selectedBucket]);
  const minAmount = 10;
  const maxAmount = Math.max(minAmount, maxTransferable);

  // Reset amounts when bucket changes
  React.useEffect(() => {
    if (fromValue && maxAmount > 0) {
      const currentAmount =
        currentActiveTab === "transfer" ? transferAmount : createBucketAmount;
      if (currentAmount > maxAmount) {
        if (currentActiveTab === "transfer") {
          onTransferAmountChange(maxAmount);
        } else {
          onCreateBucketAmountChange(maxAmount);
        }
      } else if (currentAmount < minAmount && maxAmount >= minAmount) {
        if (currentActiveTab === "transfer") {
          onTransferAmountChange(minAmount);
        } else {
          onCreateBucketAmountChange(minAmount);
        }
      }
    }
  }, [
    fromValue,
    maxAmount,
    currentActiveTab,
    transferAmount,
    createBucketAmount,
    onTransferAmountChange,
    onCreateBucketAmountChange,
  ]);

  if (!isOpen) return null;

  const isTransferValid =
    fromValue &&
    toValue &&
    transferAmount >= minAmount &&
    transferAmount <= maxAmount;
  const isCreateValid =
    fromValue &&
    createBucketAmount >= minAmount &&
    createBucketAmount <= maxAmount &&
    createBucketAsset &&
    createMarginType;

  const modalContent = (
    <div className="modal-overlay" onClick={onOverlayClick}>
      <div
        className="modal-content transfer-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Transfer Funds</h3>
        </div>

        {/* Tabs */}
        <div className="transfer-modal-tabs">
          <button
            className={`transfer-tab ${currentActiveTab === "transfer" ? "active" : ""}`}
            onClick={() => handleActiveTabChange("transfer")}
            type="button"
            disabled={isTransferring}
          >
            Transfer
          </button>
          <button
            className={`transfer-tab ${currentActiveTab === "create" ? "active" : ""}`}
            onClick={() => handleActiveTabChange("create")}
            type="button"
            disabled={isTransferring}
          >
            Create Bucket
          </button>
        </div>

        <div className="modal-body">
          {currentActiveTab === "transfer" ? (
            <>
              {/* From Selection Field */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">From</label>
                <select
                  value={fromValue}
                  onChange={(e) => onFromChange(e.target.value)}
                  className="transfer-select"
                >
                  <option value="">Select source bucket</option>
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* To Selection Field - Show when from is selected */}
              {fromValue && (
                <div className="transfer-field-group">
                  <label className="transfer-field-label">To</label>
                  <select
                    value={toValue}
                    onChange={(e) => onToChange(e.target.value)}
                    className="transfer-select"
                    disabled={!fromValue}
                  >
                    <option value="">Select destination bucket</option>
                    {options
                      .filter((option) => option.value !== fromValue)
                      .map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Amount Range Input */}
              {fromValue && maxAmount >= minAmount ? (
                <div className="transfer-field-group">
                  <div className="transfer-amount-header">
                    <label className="transfer-field-label">Amount</label>
                    <div className="transfer-available-balance">
                      Available: {formatCurrency(maxTransferable)}
                    </div>
                  </div>
                  <div
                    className="transfer-amount-display"
                    onClick={() => {
                      setNumberPadType("transfer");
                      setIsNumberPadOpen(true);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="transfer-amount-value">
                      {formatCurrency(transferAmount)}
                    </span>
                  </div>
                  <div className="transfer-amount-container">
                    <input
                      type="range"
                      min={minAmount}
                      max={maxAmount}
                      step={0.01}
                      value={transferAmount}
                      onChange={(e) =>
                        onTransferAmountChange(parseFloat(e.target.value))
                      }
                      className="transfer-amount-slider"
                      style={
                        {
                          "--slider-progress": `${((transferAmount - minAmount) / (maxAmount - minAmount)) * 100}%`,
                        } as React.CSSProperties
                      }
                    />
                    <div className="transfer-amount-labels">
                      <span>{formatCurrency(minAmount)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>
                </div>
              ) : fromValue ? (
                <div className="transfer-field-group">
                  <div className="transfer-insufficient-funds">
                    Insufficient funds. Minimum required:{" "}
                    {formatCurrency(minAmount)}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              {/* From Selection Field for Create Bucket */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">From</label>
                <select
                  value={fromValue}
                  onChange={(e) => onFromChange(e.target.value)}
                  className="transfer-select"
                >
                  <option value="">Select source bucket</option>
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Collateral Asset Selection for Create Bucket */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">Collateral Asset</label>
                <select
                  value={createBucketAsset}
                  onChange={(e) => onCreateBucketAssetChange(e.target.value)}
                  className="transfer-select"
                >
                  <option value="">Select collateral asset</option>
                  <option value="USDC">USDC</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>

              {/* Margin Type Selection Field for Create Bucket */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">Margin Type</label>
                <select
                  value={createMarginType}
                  onChange={(e) =>
                    onCreateMarginTypeChange(
                      e.target.value as "cross" | "isolated",
                    )
                  }
                  className="transfer-select"
                >
                  <option value="">Select margin type</option>
                  <option value="cross">Cross Margin</option>
                  <option value="isolated">Isolated Margin</option>
                </select>
              </div>

              {/* Amount Range Input for Create Bucket */}
              {fromValue && maxAmount >= minAmount ? (
                <div className="transfer-field-group">
                  <div className="transfer-amount-header">
                    <label className="transfer-field-label">Amount</label>
                    <div className="transfer-available-balance">
                      Available: {formatCurrency(maxTransferable)}
                    </div>
                  </div>
                  <div
                    className="transfer-amount-display"
                    onClick={() => {
                      setNumberPadType("create");
                      setIsNumberPadOpen(true);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="transfer-amount-value">
                      {formatCurrency(createBucketAmount)}
                    </span>
                  </div>
                  <div className="transfer-amount-container">
                    <input
                      type="range"
                      min={minAmount}
                      max={maxAmount}
                      step={0.01}
                      value={createBucketAmount}
                      onChange={(e) =>
                        onCreateBucketAmountChange(parseFloat(e.target.value))
                      }
                      className="transfer-amount-slider"
                      style={
                        {
                          "--slider-progress": `${((createBucketAmount - minAmount) / (maxAmount - minAmount)) * 100}%`,
                        } as React.CSSProperties
                      }
                    />
                    <div className="transfer-amount-labels">
                      <span>{formatCurrency(minAmount)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>
                </div>
              ) : fromValue ? (
                <div className="transfer-field-group">
                  <div className="transfer-insufficient-funds">
                    Insufficient funds. Minimum required:{" "}
                    {formatCurrency(minAmount)}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isTransferring}
          >
            Cancel
          </button>
          <div
            style={{
              flex: 1,
              position: "relative",
              minHeight: "48px",
            }}
          >
            <div
              className="slider-confirm-container"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                opacity: isSliderConfirmed ? 0 : 1,
                visibility: isSliderConfirmed ? "hidden" : "visible",
                transition: "opacity 0.3s ease, visibility 0.3s ease",
                pointerEvents: isSliderConfirmed ? "none" : "auto",
              }}
            >
              <SliderConfirmBar
                onConfirm={() => {
                  setIsSliderConfirmed(true);
                }}
                label="Slide to confirm"
                disabled={
                  isTransferring ||
                  (currentActiveTab === "transfer"
                    ? !isTransferValid
                    : !isCreateValid)
                }
                icon={<img src="/bconfirm.svg" />}
              />
            </div>
            <button
              onClick={() => {
                if (currentActiveTab === "transfer") {
                  onConfirm({
                    type: "transfer",
                    from: fromValue,
                    to: toValue,
                    amount: transferAmount,
                  });
                } else {
                  onConfirm({
                    type: "create",
                    from: fromValue,
                    collateralAsset: createBucketAsset,
                    marginType: createMarginType,
                    amount: createBucketAmount,
                  });
                }
              }}
              className="btn btn-primary"
              disabled={
                isTransferring ||
                (currentActiveTab === "transfer"
                  ? !isTransferValid
                  : !isCreateValid)
              }
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                opacity: isSliderConfirmed ? 1 : 0,
                visibility: isSliderConfirmed ? "visible" : "hidden",
                transition: "opacity 0.3s ease, visibility 0.3s ease",
                pointerEvents: isSliderConfirmed ? "auto" : "none",
                width: "100%",
                height: "100%",
                color: "black",
              }}
            >
              {isTransferring ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Get current amount based on number pad type
  const currentAmount =
    numberPadType === "transfer" ? transferAmount : createBucketAmount;
  const handleNumberPadConfirm = (value: number) => {
    if (numberPadType === "transfer") {
      onTransferAmountChange(value);
    } else {
      onCreateBucketAmountChange(value);
    }
    setIsNumberPadOpen(false);
  };

  return (
    <>
      {typeof document !== "undefined"
        ? createPortal(modalContent, document.body)
        : null}
      <NumberPad
        isOpen={isNumberPadOpen}
        value={currentAmount}
        min={minAmount}
        max={maxAmount}
        onConfirm={handleNumberPadConfirm}
        onCancel={() => setIsNumberPadOpen(false)}
      />
    </>
  );
}
